import { PartitionModel } from './DataSourceModel';
import {
  FieldsManagerViewOptionsModel,
  SelectedFieldModel,
  SelectedUserFields,
} from './FieldsManagerModel';

/** Global object that contains everything related to Enterprise DIY Dashboard with the exception of the partition */
export interface EnterpriseGlobalModel {
  /** Available options for the user when generating a DIY Enterprise dashboard */
  options: EnterpriseOptionsModel;
  /** Selected values by the user when populating different forms*/
  selectedValues: EnterpriseSelectedValues;
}

export interface EnterpriseOptionsModel {
  availableFieldsManagerOptions: FieldsManagerViewOptionsModel[];
}

export interface EnterpriseSelectedValues {
  /** Selected partition by the user */
  selectedPartitions: PartitionModel[];
  /** Selected or created Fields by the user associated with the partition */
  selectedEnterpriseFields: SelectedUserFields[];
}

/**
 * Helper function to update selected partitions in EnterpriseGlobalModel
 */
export const updateSelectedPartitions = (
  current: EnterpriseGlobalModel,
  partitions: PartitionModel[]
): EnterpriseGlobalModel => ({
  ...current,
  selectedValues: {
    ...current.selectedValues,
    selectedPartitions: partitions,
  },
});

/**
 * Helper function to clear selected partitions in EnterpriseGlobalModel
 */
export const clearSelectedPartitions = (current: EnterpriseGlobalModel): EnterpriseGlobalModel =>
  updateSelectedPartitions(current, []);

/**
 * Helper function to update selected enterprise fields in EnterpriseGlobalModel
 */
export const updateSelectedEnterpriseFields = (
  current: EnterpriseGlobalModel,
  fields: SelectedUserFields[]
): EnterpriseGlobalModel => ({
  ...current,
  selectedValues: {
    ...current.selectedValues,
    selectedEnterpriseFields: fields,
  },
});

/**
 * Helper function to update available fields manager options in EnterpriseGlobalModel
 */
export const updateAvailableFieldsManagerOptions = (
  current: EnterpriseGlobalModel,
  options: FieldsManagerViewOptionsModel[]
): EnterpriseGlobalModel => ({
  ...current,
  options: {
    ...current.options,
    availableFieldsManagerOptions: options,
  },
});

/**
 * Updates a single SelectedFieldModel within its partition entry.
 */
export const updateFieldInPartition = (
  current: EnterpriseGlobalModel,
  partitionGuid: string,
  fieldIndex: number,
  updatedField: SelectedFieldModel
): EnterpriseGlobalModel => {
  const updatedEnterpriseFields = current.selectedValues.selectedEnterpriseFields.map(
    (uf: SelectedUserFields) => {
      if (uf.partitionGuid !== partitionGuid) return uf;
      const fields = [...uf.fields];
      fields[fieldIndex] = updatedField;
      return { ...uf, fields: fields };
    }
  );
  return updateSelectedEnterpriseFields(current, updatedEnterpriseFields);
};
